import type { Client, CombinedError } from '@urql/core';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useClient } from 'urql';

// Direct path, not the '@shared/api' barrel: the barrel loads the urql client
// and with it the app's i18n config, which must not run inside unit tests.
import { SILENT_ERRORS_CONTEXT } from '@shared/api/silent-errors';

import { ENPS_DISMISS, ENPS_MARK_SHOWN, ENPS_SUBMIT } from '../graphql/mutations';
import { ENPS_PENDING_SURVEY } from '../graphql/queries';
import type { EnpsPendingSurvey, EnpsSlot } from '../types';

import {
  enqueuePendingSubmission,
  hasPendingSubmission,
  readPendingSubmissions,
  removePendingSubmission,
  type PendingSubmission,
} from './pending-submissions';

// The survey runs in the background of whatever page the user is on: no
// failure here may raise a toast (spec: "the user must not be interrupted").
const SILENT = { ...SILENT_ERRORS_CONTEXT, requestPolicy: 'network-only' as const };

// Errors that will never succeed on retry: the slot is gone (quarter over,
// already dismissed), the input is invalid, or the user is not a team user.
// Anything else (network, timeout, 5xx surfaced as a GraphQL error) is kept.
const DEFINITIVE_ERROR_CODES = new Set([
  'BAD_REQUEST',
  'BAD_USER_INPUT',
  'FORBIDDEN',
  'GRAPHQL_VALIDATION_FAILED',
]);

export function isDefinitiveFailure(error: CombinedError): boolean {
  if (error.networkError) return false;
  return (
    error.graphQLErrors.length > 0 &&
    error.graphQLErrors.every((err) => DEFINITIVE_ERROR_CODES.has(err.extensions['code'] as string))
  );
}

/** Sends one queued answer; it leaves the queue once accepted or definitively rejected. */
async function sendSubmission(client: Client, item: PendingSubmission): Promise<void> {
  const result = await client.mutation(ENPS_SUBMIT, { input: item.input }, SILENT).toPromise();
  if (!result.error || isDefinitiveFailure(result.error)) removePendingSubmission(item);
}

async function flushPendingSubmissions(client: Client, userId: string): Promise<void> {
  const mine = readPendingSubmissions().filter((item) => item.userId === userId);
  for (const item of mine) {
    await sendSubmission(client, item);
  }
}

function slotOf(survey: EnpsSlot): EnpsSlot {
  return { year: survey.year, quarter: survey.quarter };
}

function slotKey(userId: string, slot: EnpsSlot): string {
  return `${userId}:${String(slot.year)}-Q${String(slot.quarter)}`;
}

export interface UseEnpsSurveyResult {
  survey: EnpsPendingSurvey | null;
  open: boolean;
  setOpen: (open: boolean) => void;
  submit: (score: number, comment: string) => void;
  dismiss: () => void;
}

/**
 * Drives the eNPS survey (DEV-4077) against the DEV-4075 backend. On app load
 * it asks whether a survey is due — the backend owns every scheduling rule
 * (60-day onboarding, one per quarter, random day, 30-day gap, multi-tenant
 * split, team-user-only access) — and if so opens the modal and records it as
 * shown, which uses up the quarter's slot.
 */
export function useEnpsSurvey(userId: string | null, enabled: boolean): UseEnpsSurveyResult {
  const client = useClient();
  const { i18n } = useTranslation();
  const [survey, setSurvey] = useState<EnpsPendingSurvey | null>(null);
  const [open, setOpen] = useState(false);
  // Once answered, closing the "thanks" state must not also record a dismiss.
  const answeredRef = useRef(false);
  // A slot opens at most once per page life, even if the effect re-runs
  // (StrictMode, a token refresh flipping `enabled`).
  const openedSlotRef = useRef<string | null>(null);
  // dismiss/submit wait for markShown so a late markShown can never land after
  // them on the server.
  const shownRef = useRef<Promise<unknown>>(Promise.resolve());

  useEffect(() => {
    if (!enabled || !userId) return;
    let cancelled = false;
    // A function, so the check isn't narrowed away across the awaits below.
    const isCancelled = () => cancelled;

    void (async () => {
      await flushPendingSubmissions(client, userId);
      if (isCancelled()) return;

      const result = await client.query(ENPS_PENDING_SURVEY, {}, SILENT).toPromise();
      const pending = result.data?.investbanq?.enps?.pendingSurvey ?? null;
      if (isCancelled() || !pending) return;

      const key = slotKey(userId, pending);
      if (openedSlotRef.current === key) return;
      // Already answered, still waiting to be delivered — don't ask twice.
      if (hasPendingSubmission(userId, pending.year, pending.quarter)) return;

      // Load the copy before opening, so the modal never suspends the app.
      await i18n.loadNamespaces('enps');
      if (isCancelled() || openedSlotRef.current === key) return;

      openedSlotRef.current = key;
      answeredRef.current = false;
      shownRef.current = client
        .mutation(ENPS_MARK_SHOWN, { input: slotOf(pending) }, SILENT)
        .toPromise();
      setSurvey(pending);
      setOpen(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [client, enabled, i18n, userId]);

  // An answer that failed on a flaky connection goes out as soon as it is back.
  useEffect(() => {
    if (!enabled || !userId) return;
    const onOnline = () => {
      void flushPendingSubmissions(client, userId);
    };
    window.addEventListener('online', onOnline);
    return () => {
      window.removeEventListener('online', onOnline);
    };
  }, [client, enabled, userId]);

  const submit = useCallback(
    (score: number, comment: string) => {
      if (!survey || !userId || answeredRef.current) return;
      answeredRef.current = true;
      const item: PendingSubmission = {
        userId,
        input: { ...slotOf(survey), score, comment: comment || null },
        queuedAt: Date.now(),
      };
      enqueuePendingSubmission(item);
      void shownRef.current.then(() => sendSubmission(client, item));
    },
    [client, survey, userId],
  );

  const dismiss = useCallback(() => {
    if (!survey || answeredRef.current) return;
    answeredRef.current = true;
    // Best effort: markShown already used up the slot, so a lost dismiss still
    // reads as a no response on the backend.
    const input = slotOf(survey);
    void shownRef.current.then(() => client.mutation(ENPS_DISMISS, { input }, SILENT).toPromise());
  }, [client, survey]);

  return { survey, open, setOpen, submit, dismiss };
}
